import type { IEntityPermissions } from "@av/config/api-config/permissions/entity-permissions.interface";
import { dayjs } from "@av/config/extended-dayjs";

type PathSegment = string | number;

type FlatField = {
  fieldPath: PathSegment[];
  value: unknown;
};

/**
 * Maps the form data model to the external submitted payload
 */
export class SubmittedFieldsMapper {
  #permissions?: IEntityPermissions;

  constructor(permissions?: IEntityPermissions) {
    this.#permissions = permissions;
  }

  map(model: object): Record<string, unknown> {
    const leafFields = this.#collectLeafFields(model);

    const result = leafFields.reduce(
      (acc, { fieldPath, value }) => {
        if (this.#isFieldAllowed(fieldPath)) {
          this.#setValueAtPath(acc, fieldPath, value);
        }

        return acc;
      },
      {} as Record<string, unknown>
    );

    return result;
  }

  #isFieldAllowed(fieldPath: PathSegment[]): boolean {
    if (!this.#permissions) {
      return true;
    }

    const path = fieldPath.map(String).join(".");

    return this.#permissions.isFieldEditable(path) && this.#permissions.isFieldViewable(path);
  }

  /**
   * Recursively travels the object tree and
   * sgenerates a collection of field and value tuples
   *
   * @param value the actual value
   * @param currentPath field path from root to leef
   * @returns
   */
  #collectLeafFields(value: unknown, currentPath: PathSegment[] = []): FlatField[] {
    if (Array.isArray(value)) {
      return value.flatMap((element, idx) => {
        const finalPath = currentPath.concat(String(idx));

        return this.#collectLeafFields(element, finalPath);
      });
    }

    if (typeof Blob !== "undefined" && value instanceof Blob) {
      return currentPath.length === 0 ? [] : [{ fieldPath: currentPath, value }];
    }

    if (dayjs.isDayjs(value)) {
      return currentPath.length === 0 ? [] : [{ fieldPath: currentPath, value }];
    }

    if (value != null && typeof value === "object") {
      return Object.entries(value).flatMap(([key, nestedValue]) => {
        let keyPath: Array<string | number> = [key];

        if (key.includes(".")) {
          keyPath = key.split(".").map((part) => (isNaN(Number(part)) ? part : Number(part)));
        }

        const finalPath = currentPath.concat(keyPath);

        return this.#collectLeafFields(nestedValue, finalPath);
      });
    }

    if (currentPath.length === 0) {
      return [];
    }

    return [{ fieldPath: currentPath, value }];
  }

  /**
   * Based on the field path sets the value on the provided object
   * @param target
   * @param fieldPath
   * @param value
   */
  #setValueAtPath(target: Record<string, unknown>, fieldPath: PathSegment[], value: unknown) {
    let current = target as Record<string | number, unknown>;

    fieldPath.forEach((segment, index) => {
      const isLastSegment = index === fieldPath.length - 1;

      if (isLastSegment) {
        current[segment] = value;

        return;
      }

      const nextSegment = fieldPath[index + 1];
      const isNextNumber = isNaN(Number(nextSegment));
      const existingValue = current[segment];

      if (typeof existingValue !== "object") {
        current[segment] = isNextNumber ? {} : [];
      }

      current = current[segment] as Record<string | number, unknown>;
    });
  }
}
